import {
  QUERY_b2bDepartmentsNavigationFetchDepartments,
  QUERY_b2bPositionsSettingsPageFetchPositionsByDepartmentId,
} from 'queries';
import {
  B2bDepartmentsNavigationFetchDepartmentsQuery,
  B2bCustomer,
  B2bPositionsSettingsPageFetchPositionsByDepartmentIdQuery,
} from 'types/graphql';
import {
  IApiPostB2BNewDepartmentPayload,
  IEditDepartmentFormSchema,
  IFetchDepartmentsData,
  INewDepartmentFormSchema,
  IPosition,
} from 'pages/Settings/pages/Positions/B2BPositionsSettingsPage/B2BPositionsSettingsPage.types';

import { UseFormBehaviors_Options } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { uniqBy } from 'utils/uniqBy';

export const getPositionNameKeyByPositionId = (positionId: string) => `${positionId}_positionName`;
export const getIncludedKeyByPositionId = (positionId: string) => `${positionId}_included`;
export const getOpenShiftPolicyKeyByPositionId = (positionId: string) =>
  `${positionId}_openShiftPolicy`;
export const getIncludedKeys = (keys: string[]) =>
  keys.filter((value) => {
    const regex = new RegExp('.*_included');
    return regex.test(value);
  });
export const getPositionNameKeys = (keys: string[]) =>
  keys.filter((value) => {
    const regex = new RegExp('.*_positionName');
    return regex.test(value);
  });

export const getMappedPositionKeyByPositionId = (positionId: string) =>
  `${positionId}_mappedPosition`;
export const getMappedPositionKeyByIndex = (index: number) => `${index}_mappedPosition`;

const getNewPositions = (formValues: IEditDepartmentFormSchema | INewDepartmentFormSchema) => {
  const filteredPositions = formValues.newPositions.filter((position) => !!position);

  if (filteredPositions.length)
    return filteredPositions.map((position, index) => {
      const mappedPositionId = formValues.mappedPositions?.[index];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result: any = {
        name: position,
        open_shift_policy: formValues.openShiftPolicies[index],
      };

      if (mappedPositionId && mappedPositionId !== '') {
        result.marketplace_position_id = Number(mappedPositionId);
      }

      return result;
    });
};

export const newFormToPayloadTransformer = (
  formValues: INewDepartmentFormSchema,
): IApiPostB2BNewDepartmentPayload => ({
  department_create: {
    name: formValues.name,
  },
  new_positions: getNewPositions(formValues),
});

export const editFormToPayloadTransformer = (
  departmentId: number,
  formValues: IEditDepartmentFormSchema,
) => {
  return {
    department_update: {
      id: departmentId,
      name: formValues.name,
      status: 'active',
      positions_attributes: getIncludedKeys(Object.keys(formValues))
        .map((key) => key.split('_')[0])
        .map((positionId) => {
          const mappedPositionId = formValues[getMappedPositionKeyByPositionId(positionId)];
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const result: any = {
            id: Number.parseInt(positionId, 10),
            status: formValues[getIncludedKeyByPositionId(positionId)] ? 'active' : 'in_active',
            name: formValues[getPositionNameKeyByPositionId(positionId)],
            open_shift_policy: formValues[getOpenShiftPolicyKeyByPositionId(positionId)],
            error: false,
          };

          if (mappedPositionId && mappedPositionId !== '') {
            result.marketplace_attributes = {
              marketplace_position_id: Number(mappedPositionId),
            };
          }

          return result;
        }),
    },
    new_positions: getNewPositions(formValues),
  };
};

export const b2bEditDepartmentFormConfig = ({
  name,
  positions,
  onSubmit,
  validations,
}: {
  name: string;
  positions: IPosition[];
  onSubmit: UseFormBehaviors_Options<IEditDepartmentFormSchema>['onSubmit'];
  validations: UseFormBehaviors_Options<IEditDepartmentFormSchema>['validations'];
}): UseFormBehaviors_Options<IEditDepartmentFormSchema> => {
  const selectedPositionIds = positions.map((position) => position.positionId);
  const selectedPositionsInitialState: IEditDepartmentFormSchema = {
    name,
    newPositions: [],
    locationsByPosition: [],
    updatedAt: '',
    openShiftPolicies: [],
    mappedPositions: [],
  };
  positions.forEach((position) => {
    selectedPositionsInitialState[getIncludedKeyByPositionId(position.positionId)] =
      selectedPositionIds.includes(position.positionId);
    selectedPositionsInitialState[getPositionNameKeyByPositionId(position.positionId)] =
      position.positionName;
    selectedPositionsInitialState[`${position.positionId}_openShiftPolicy`] =
      position.openShiftPolicy;
    selectedPositionsInitialState[getMappedPositionKeyByPositionId(position.positionId)] =
      position.mappedPosition || '';
  });

  return {
    initialState: selectedPositionsInitialState,
    // type: 'EDIT',
    validations,
    onSubmit,
  };
};

export const fetchDepartmentsQueryConfig = {
  query: QUERY_b2bDepartmentsNavigationFetchDepartments,
  key: 'B2B_DEPARTMENTS_NAVIGATION_FETCH_DEPARTMENTS',
  responseTransformer: (
    response: B2bDepartmentsNavigationFetchDepartmentsQuery,
  ): IFetchDepartmentsData => {
    const data = response?.me as B2bCustomer;
    const serviceablePositions = data?.serviceablePositions || [];
    return {
      departments:
        uniqBy(
          serviceablePositions
            ?.map((position) => position.department)
            .filter((department) => !!department)
            .map((department) => ({
              value: `${department!.id}`,
              label: department!.name,
              name: 'department',
              key: department!.id,
            })),
          (item) => item.value,
        ) || [],
    };
  },
};

export const fetchPositionsByDepartmentIdQueryConfig = (departmentId: number) => ({
  query: QUERY_b2bPositionsSettingsPageFetchPositionsByDepartmentId,
  key: `B2B_DEPARTMENTS_SETTINGS_PAGE_FETCH_POSITIONS_BY_ID_${departmentId}`,
  responseTransformer: (response: B2bPositionsSettingsPageFetchPositionsByDepartmentIdQuery) => {
    const data = response?.me as B2bCustomer;
    return {
      positions: data?.serviceablePositions
        .filter((position) => !!position.department)
        .map((position) => ({
          departmentId: `${position.department!.id}`,
          departmentName: position.department!.name,
          positionId: `${position.id}`,
          positionName: position.name,
          mappedPosition: position.marketplacePosition?.displayId
            ? String(position.marketplacePosition.displayId)
            : '',
          locationsPerPosition: position.serviceableCommunities,
          updatedAt: position.updatedAt,
          openShiftPolicy: position.openShiftPolicy,
        }))
        .filter((position) => position.departmentId === `${departmentId}`),
      marketplacePositions: (data?.marketplacePositions || []).map((pos) => ({
        displayId: String(pos.displayId),
        name: pos.name,
      })),
    };
  },
});
